import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { buildLocalBusiness, buildFaqPage } from "@/lib/jsonLd";
import { ArrowUpRight, ArrowRight, ArrowDown, ShieldCheck, Award, Clock, Star, Plus, Minus } from "lucide-react";

// A curated wood/craftsmanship photo — a craftsman planing wood, shavings visible.
// Luigi can override it from /admin/parametres (champ hero_image_url).
const HERO_IMAGE_FALLBACK = "https://images.pexels.com/photos/3637837/pexels-photo-3637837.jpeg?auto=compress&cs=tinysrgb&w=2400";
const HERO_EYEBROW_FALLBACK = "Maison Luigi Chauveau  ·  Édition mmxxvi";
const ATELIER_IMAGE = "https://images.pexels.com/photos/7492582/pexels-photo-7492582.jpeg";

const TARGETS = [
  {
    eyebrow: "01",
    title: "Particuliers",
    desc: "Cuisines, dressings, bibliothèques, escaliers, agencements complets — sur-mesure, en matériaux nobles.",
    cta: "Pour un projet domestique",
    href: "/contact?cible=particulier",
    image: "https://images.unsplash.com/photo-1760438492655-63efac635f61",
  },
  {
    eyebrow: "02",
    title: "Cabinets d'expertise",
    desc: "Coordination de dossiers de sinistre. Constat sous cinq jours ouvrés, devis sous huit jours, décennale et RC Pro à jour.",
    cta: "Pour les cabinets d'expertise",
    href: "/cabinets-d-expertise",
    accent: true,
    image: "https://images.unsplash.com/photo-1771574205963-0c1d84ac7354",
  },
  {
    eyebrow: "03",
    title: "Restaurateurs & commerçants",
    desc: "Agencement professionnel et remise en état après sinistre, en calendrier compatible avec la reprise d'activité.",
    cta: "Pour un établissement",
    href: "/contact?cible=professionnel",
    image: "https://images.unsplash.com/photo-1617262869510-3793b141c294",
  },
];

const PRINCIPES = [
  { num: "I", title: "L'écoute", body: "Le projet du client passe avant l'expression de l'artisan." },
  { num: "II", title: "La matière", body: "Travailler le chêne, le noyer, le frêne, le hêtre. Du bois vivant." },
  { num: "III", title: "La précision", body: "Le millimètre, partout. Aucun détail abandonné en chemin." },
  { num: "IV", title: "Le délai", body: "La parole tenue, surtout quand la compagnie d'assurance attend." },
];

const TRUST_BADGES = [
  { icon: Award, label: "Compagnons du Devoir" },
  { icon: ShieldCheck, label: "Décennale & RC Pro à jour" },
  { icon: Clock, label: "Réponse sous 48 heures" },
];

const STATS = [
  { kpi: "2005", label: "Entré chez les Compagnons du Devoir" },
  { kpi: "10+", label: "Années d'exercice à son compte" },
  { kpi: "5j", label: "Constat sur place pour les sinistres" },
  { kpi: "8j", label: "Devis remis aux cabinets d'expertise" },
];

const FAQS = [
  {
    q: "Sous quel délai recevoir un devis ?",
    a: "Pour les particuliers : sous une à deux semaines après la visite. Pour les cabinets d'expertise : sous huit jours ouvrés, conformément aux grilles des compagnies.",
  },
  {
    q: "Quelle zone géographique couvrez-vous ?",
    a: "Pour les particuliers : Loire-Atlantique exclusivement — Nantes, Saint-Nazaire, La Baule, Pornic, Châteaubriant et alentours. Pour les cabinets d'expertise d'assurance, je peux me déplacer au-delà selon les délais à tenir.",
  },
  {
    q: "Quels sont vos tarifs ?",
    a: "Chaque projet est devisé après visite et relevé au millimètre. Pas de catalogue, pas de prix au m² standard — du sur-mesure véritable, en matière franche.",
  },
  {
    q: "Travaillez-vous avec les compagnies d'assurance ?",
    a: "Oui — c'est même un segment central de mon activité. Décennale et RC Pro à jour, devis conformes aux grilles, coordination directe avec le cabinet et le sinistré.",
  },
  {
    q: "Quel est le délai d'un chantier ?",
    a: "Variable selon la complexité — d'une semaine pour un agencement ponctuel à plusieurs mois pour une rénovation complète. Le calendrier est arrêté au devis, et tenu.",
  },
  {
    q: "Acceptez-vous les petits projets ?",
    a: "Oui, à condition qu'il y ait un vrai travail de menuiserie. Une cuisine, un dressing, une bibliothèque, un escalier — toujours.",
  },
];

export default function Home() {
  const [realisations, setRealisations] = useState([]);
  const [site, setSite] = useState({});
  const [temoignages, setTemoignages] = useState([]);
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    api
      .get("/realisations")
      .then((r) => setRealisations(r.data))
      .catch(() => setRealisations([]));
    api
      .get("/site/settings")
      .then((r) => setSite(r.data || {}))
      .catch(() => setSite({}));
    api
      .get("/temoignages")
      .then((r) => setTemoignages(r.data || []))
      .catch(() => setTemoignages([]));
    api
      .get("/site/business")
      .then((r) => setBusiness(r.data))
      .catch(() => setBusiness(null));
  }, []);

  const phare = realisations.find((r) => r.est_phare);
  const others = realisations.filter((r) => !r.est_phare).slice(0, 2);
  // Hero is ALWAYS a carpentry/craftsmanship image — never the restaurant.
  // Luigi can customise it from /admin/parametres (champ hero_image_url).
  const heroImage = site.hero_image_url || HERO_IMAGE_FALLBACK;

  const jsonLd = [buildLocalBusiness(business), buildFaqPage(FAQS)].filter(Boolean);

  return (
    <div data-testid="home-page">
      <Seo
        title="Menuisier agenceur, l'art du sur-mesure"
        description="Luigi Chauveau, menuisier agenceur en Loire-Atlantique. Agencements sur-mesure en matières franches — chêne, noyer, frêne, hêtre. Particuliers (44), cabinets d'expertise d'assurance, restaurateurs. Devis sous huit jours."
        path="/"
        jsonLd={jsonLd}
      />
      {/* HERO — cinematic full-bleed */}
      <section
        className="relative h-screen min-h-[640px] max-h-[920px] overflow-hidden"
        data-testid="home-hero"
      >
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/75" />

        <div className="relative h-full flex flex-col z-10">
          <div className="flex-1" />
          <div className="max-w-[1500px] mx-auto w-full px-5 md:px-10 pb-10 md:pb-14">
            <h1
              className="h-display text-white text-[40px] sm:text-5xl md:text-[72px] lg:text-[88px] text-balance leading-[0.96] max-w-[1100px] fade-up fade-up-1"
              data-testid="hero-title"
            >
              Menuisier agenceur,
              <br />
              <span className="serif-italic text-[hsl(var(--gold-soft))]">l'art du sur-mesure</span>.
            </h1>
            <div className="mt-7 md:mt-10 grid md:grid-cols-12 gap-6 items-end fade-up fade-up-2">
              <p className="md:col-span-6 text-[14px] md:text-[15px] text-white/85 max-w-lg leading-relaxed">
                Formé chez les Compagnons du Devoir. Artisan depuis 2015. Agencements
                sur-mesure en matériaux nobles.
              </p>
              <div className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 bg-background text-foreground px-6 py-4 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-primary hover:text-background transition-colors"
                  data-testid="hero-cta-contact"
                >
                  Demander un devis <ArrowUpRight size={14} />
                </Link>
                <Link
                  to="/realisations"
                  className="inline-flex items-center gap-3 border border-white/70 text-white px-6 py-4 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-white hover:text-foreground transition-colors"
                  data-testid="hero-cta-realisations"
                >
                  Réalisations
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom-left scroll cue */}
        <div className="absolute left-5 md:left-10 bottom-5 md:bottom-6 hidden md:flex items-center gap-3 text-white/50 z-10">
          <ArrowDown size={13} />
          <span className="text-[10px] tracking-[0.3em] uppercase">Lire</span>
        </div>
      </section>

      {/* TRUST STRIP — clair sous le hero */}
      <section className="border-b border-border bg-background" data-testid="home-trust-strip">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-5 md:py-6">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3">
            {TRUST_BADGES.map((b, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`trust-badge-${i}`}>
                <b.icon size={16} className="text-[hsl(var(--gold))]" />
                <span className="text-[11px] md:text-[11.5px] tracking-[0.18em] uppercase text-foreground/80">{b.label}</span>
              </div>
            ))}
            <a
              href="tel:+33761051061"
              className="hidden md:inline-flex items-center gap-2 text-[11.5px] tracking-[0.2em] uppercase font-medium text-primary link-underline-static"
              data-testid="trust-strip-tel"
            >
              07 61 05 10 61
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAR — chiffres-clés en noir */}
      <section className="border-b border-border bg-foreground text-background" data-testid="home-stats">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-background/10">
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="bg-foreground p-5 md:p-6 flex flex-col gap-2" data-testid={`stat-${i}`}>
                <span className="h-display text-3xl md:text-[40px] leading-none text-background">{s.kpi}</span>
                <span className="text-[11px] tracking-[0.18em] uppercase text-background/55 leading-snug">{s.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO — typographic statement */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="home-intro">
        <Reveal>
          <p className="text-eyebrow-gold mb-5">i — Manifeste</p>
        </Reveal>
        <Reveal delay={120}>
          <p className="h-display text-[28px] sm:text-[38px] md:text-[52px] leading-[1.1] text-balance max-w-5xl">
            Une pièce bien agencée tient la conversation,{" "}
            <span className="serif-italic text-primary">pas l'attention</span>. Elle se rappelle au regard, dix ans
            après la pose, par la justesse des proportions et la noblesse de la matière.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <div className="mt-10 md:mt-12 max-w-3xl grid md:grid-cols-5 gap-6 items-end">
            <div className="md:col-span-3">
              <div className="gold-rule w-24 mb-6" />
              <p className="text-[15.5px] md:text-[16.5px] leading-[1.7] text-foreground/85">
                Formé chez les Compagnons du Devoir. Artisan depuis 2015. Je conçois et je pose des agencements
                sur-mesure dans des matériaux nobles — chêne, noyer, frêne, hêtre — et je tiens mes délais. C'est tout.
              </p>
            </div>
            <div className="md:col-span-2 md:pl-8 md:border-l md:border-border">
              <p className="text-serial mb-2">Coordonnées</p>
              <ul className="space-y-1.5 text-[14px]">
                <li><a className="link-underline-static" href="tel:+33761051061">07 61 05 10 61</a></li>
                <li><a className="link-underline-static" href="mailto:contact@luigichauveau.fr">contact@luigichauveau.fr</a></li>
              </ul>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PROJET PHARE — full-bleed editorial */}
      {phare && (
        <section className="bg-foreground text-background relative" data-testid="home-flagship">
          <div className="max-w-[1500px] mx-auto px-0 md:px-10 pt-20 md:pt-24 pb-20 md:pb-24">
            <div className="px-5 md:px-0 mb-8 grid md:grid-cols-12 gap-8 items-end">
              <Reveal as="div" className="md:col-span-6">
                <p className="text-eyebrow-gold mb-3">ii — Projet phare · {phare.annee}</p>
                <h2 className="h-display text-4xl md:text-6xl lg:text-[72px] leading-[1.02]">
                  {phare.titre}
                </h2>
              </Reveal>
              <Reveal as="div" delay={140} className="md:col-span-5 md:col-start-8 md:pb-4">
                {phare.sous_titre && (
                  <p className="serif-italic text-2xl md:text-3xl text-background/80 mb-5 leading-snug">
                    {phare.sous_titre}
                  </p>
                )}
                <p className="text-[15px] leading-relaxed text-background/75">{phare.chapeau}</p>
                <Link
                  to={`/realisations/${phare.slug}`}
                  className="mt-7 inline-flex items-center gap-3 border border-[hsl(var(--gold))] text-[hsl(var(--gold-soft))] px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-[hsl(var(--gold))] hover:text-foreground transition-colors"
                  data-testid="flagship-cta"
                >
                  Lire le dossier <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
            <Reveal image>
              <div className="image-zoom aspect-[16/9] overflow-hidden">
                <img src={phare.photo_couverture} alt={phare.titre} className="w-full h-full object-cover" />
              </div>
            </Reveal>
            {/* Caption row */}
            <div className="px-5 md:px-0 mt-6 grid md:grid-cols-12 gap-6 text-[11px] tracking-[0.2em] uppercase text-background/60">
              <div className="md:col-span-3">
                <p className="text-[hsl(var(--gold))] mb-1">Catégorie</p>
                <p>{phare.categorie === "sinistre" ? "Sinistre · Coordination expert" : phare.categorie}</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-[hsl(var(--gold))] mb-1">Matériaux</p>
                <p className="lowercase tracking-normal text-[12.5px] text-background/80 serif-italic">
                  {phare.materiaux?.join(" · ")}
                </p>
              </div>
              <div className="md:col-span-3">
                <p className="text-[hsl(var(--gold))] mb-1">Lieu</p>
                <p>Loire-Atlantique</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-[hsl(var(--gold))] mb-1">Année</p>
                <p>{phare.annee}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* QUATRE PRINCIPES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="home-principes">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <Reveal as="div" className="md:col-span-5">
            <p className="text-eyebrow-gold mb-3">iii — Quatre principes</p>
            <h2 className="h-display text-4xl md:text-5xl leading-[1.02]">
              Ce qui ne se négocie <span className="serif-italic text-primary">jamais</span>.
            </h2>
          </Reveal>
          <Reveal as="div" delay={120} className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-[15px] text-muted-foreground max-w-md leading-relaxed">
              Un cadre simple, tenu projet après projet, qui explique pourquoi mes clients
              reviennent et pourquoi les cabinets d'expertise me confient leurs dossiers les plus exigeants.
            </p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-4 gap-px bg-border">
          {PRINCIPES.map((p, i) => (
            <Reveal key={p.num} delay={i * 80} className="bg-background p-8 md:p-10 min-h-[260px] flex flex-col justify-between" data-testid={`principe-${i}`}>
              <span className="serif-italic text-5xl md:text-6xl text-[hsl(var(--gold))]">{p.num}</span>
              <div>
                <h3 className="font-serif text-3xl md:text-[34px] leading-tight mb-3">{p.title}</h3>
                <p className="text-[13.5px] text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3 CIBLES — refined cards with image reveal on hover */}
      <section className="border-y border-border bg-muted/30" data-testid="home-targets">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 mb-12">
            <Reveal as="div" className="md:col-span-5">
              <p className="text-eyebrow-gold mb-3">iv — Pour qui</p>
              <h2 className="h-display text-4xl md:text-5xl leading-[1.02]">
                Trois manières <br />
                d'<span className="serif-italic text-primary">entrer</span>.
              </h2>
            </Reveal>
            <Reveal as="div" delay={120} className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-[15px] text-muted-foreground max-w-md leading-relaxed">
                Chaque interlocuteur a ses contraintes — j'y réponds avec la même méthode et le même soin du détail.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border">
            {TARGETS.map((t, i) => (
              <Reveal key={t.eyebrow} delay={i * 100}>
                <Link
                  to={t.href}
                  className={`group relative block bg-background hover:bg-foreground hover:text-background transition-colors duration-500 h-full ${
                    t.accent ? "ring-1 ring-inset ring-primary/30" : ""
                  }`}
                  data-testid={`target-card-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={t.image}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.08] grayscale group-hover:grayscale-0"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7 md:p-9 min-h-[260px] flex flex-col">
                    <div className="flex items-start justify-between mb-7">
                      <span className="text-[11px] tracking-[0.3em] uppercase text-[hsl(var(--gold))]">{t.eyebrow}</span>
                      <ArrowUpRight
                        size={18}
                        className="opacity-50 group-hover:opacity-100 transition-all group-hover:-translate-y-1 group-hover:translate-x-1"
                      />
                    </div>
                    <h3 className="font-serif text-3xl md:text-[38px] mb-4 leading-tight">{t.title}</h3>
                    <p className="text-[13.5px] leading-relaxed opacity-70 group-hover:opacity-90 transition-opacity mb-7 max-w-sm">
                      {t.desc}
                    </p>
                    <span className="mt-auto text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--gold))]">
                      {t.cta} →
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES — preuve sociale, éditable depuis le backoffice */}
      {temoignages.length > 0 && (
        <section className="border-y border-border bg-background" data-testid="home-temoignages">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
            <div className="grid md:grid-cols-12 gap-10 mb-12">
              <Reveal as="div" className="md:col-span-6">
                <p className="text-eyebrow-gold mb-3">v — Paroles de clients</p>
                <h2 className="h-display text-4xl md:text-5xl leading-[1.02]">
                  Ce qu'<span className="serif-italic text-primary">on en dit</span>.
                </h2>
              </Reveal>
              <Reveal as="div" delay={140} className="md:col-span-5 md:col-start-8 self-end">
                <p className="text-[15px] text-muted-foreground max-w-md leading-relaxed">
                  Particuliers, restaurateurs, cabinets d'expertise — des voix
                  qui résument dix ans d'un même engagement.
                </p>
              </Reveal>
            </div>

            <div className="grid md:grid-cols-2 gap-px bg-border">
              {temoignages.map((t, i) => (
                <Reveal
                  key={t.id || i}
                  delay={i * 100}
                  className="bg-background p-8 md:p-12 flex flex-col gap-7 min-h-[280px]"
                  data-testid={`temoignage-${i}`}
                >
                  <div className="flex items-center gap-1.5 text-[hsl(var(--gold))]">
                    {Array.from({ length: Math.max(1, Math.min(5, t.note || 5)) }).map((_, s) => (
                      <Star key={s} size={13} fill="currentColor" stroke="currentColor" />
                    ))}
                  </div>
                  <p className="font-serif italic text-2xl md:text-[26px] leading-[1.4] text-foreground/90 flex-1">
                    « {t.texte} »
                  </p>
                  <div className="pt-5 border-t border-border">
                    <p className="text-[14px] font-medium">{t.auteur}</p>
                    {t.contexte && (
                      <p className="text-[12px] tracking-[0.12em] uppercase text-muted-foreground mt-1">{t.contexte}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE — large editorial pull */}
      <section className="bg-foreground text-background py-24 md:py-32" data-testid="home-quote">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-eyebrow-gold mb-5">vi — Parole d'artisan</p>
          </Reveal>
          <Reveal delay={140}>
            <blockquote className="h-display text-3xl sm:text-5xl md:text-[64px] leading-[1.02] text-balance max-w-5xl">
              <span className="serif-italic text-[hsl(var(--gold-soft))]">«</span> Ce que je préfère
              aujourd'hui ? La satisfaction du client en fin de chantier, et le fait de travailler des{" "}
              <span className="serif-italic text-[hsl(var(--gold-soft))]">matériaux nobles</span>.
              <span className="serif-italic text-[hsl(var(--gold-soft))]"> »</span>
            </blockquote>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-12 flex items-center gap-5">
              <div className="gold-rule w-12 opacity-80" />
              <p className="text-[12px] tracking-[0.25em] uppercase text-background/70">Luigi Chauveau · Atelier 2026</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ — désamorce les objections silencieuses */}
      <section className="border-y border-border bg-muted/30" data-testid="home-faq">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 mb-10">
            <Reveal as="div" className="md:col-span-5">
              <p className="text-eyebrow-gold mb-3">ix — Questions fréquentes</p>
              <h2 className="h-display text-4xl md:text-5xl leading-[1.02]">
                Ce qu'on me <span className="serif-italic text-primary">demande</span>.
              </h2>
            </Reveal>
            <Reveal as="div" delay={140} className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-[15px] text-muted-foreground max-w-md leading-relaxed">
                Délais, tarifs, zone d'intervention, dossiers d'assurance —
                voici les réponses, sans détour.
              </p>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-1 border-t border-border">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
          <div className="mt-14 flex flex-wrap items-baseline gap-6">
            <p className="text-[14px] text-muted-foreground">Une question qui n'est pas listée ?</p>
            <Link to="/contact" className="text-[12px] tracking-[0.22em] uppercase link-underline-static text-primary" data-testid="faq-cta">
              Poser ma question →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="home-cta-final">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <Reveal as="div" className="md:col-span-8">
            <p className="text-eyebrow-gold mb-6">x — Engager une conversation</p>
            <h2 className="h-display text-6xl md:text-[112px] leading-[0.94] text-balance">
              Un projet en tête,
              <br />
              un sinistre en cours,
              <br />
              <span className="serif-italic text-primary">parlons-en</span>.
            </h2>
          </Reveal>
          <Reveal as="div" delay={140} className="md:col-span-4 md:col-start-9 space-y-7">
            <p className="text-[14.5px] text-muted-foreground leading-relaxed">
              Le formulaire de contact adapte ses champs selon votre profil. Réponse rapide,
              attestations transmises sans délai.
            </p>
            <div className="flex flex-col gap-4 items-start">
              <Link to="/contact" className="btn-ink" data-testid="footer-cta-contact">
                Formuler une demande <ArrowUpRight size={14} />
              </Link>
              <a href="tel:+33761051061" className="text-[12px] tracking-[0.22em] uppercase link-underline-static text-foreground">
                Ou appeler — 07 61 05 10 61
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-border py-6" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 text-left group"
        aria-expanded={open}
      >
        <span className="font-serif text-xl md:text-2xl leading-tight pt-0.5 group-hover:text-primary transition-colors">
          {q}
        </span>
        <span className="shrink-0 mt-1 text-[hsl(var(--gold))]">
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      {open && (
        <p className="mt-5 text-[14.5px] leading-[1.7] text-muted-foreground max-w-2xl">
          {a}
        </p>
      )}
    </div>
  );
}
